/**
 * Estrutura do menu lateral.
 *
 * Mora AQUI, e não dentro do `Sidebar`, por um motivo só: o guardrail
 * (`menu.test.ts`) precisa importar a árvore para falhar no BUILD quando alguém
 * acrescentar um item sem declarar `permission`. Um aviso em runtime não serve —
 * o erro que se quer pegar é o do item novo esquecido, e ele apareceria no
 * console de quem já está logado, tarde demais.
 *
 * ⛔ REGRA: TODO item declara `permission`, em qualquer nível (pai, filho, neto).
 * O filtro do menu FALHA FECHADO: item sem `permission` fica escondido, nunca
 * exibido. Esquecer passa a sumir a tela (erro visível e inofensivo) em vez de
 * expô-la (erro silencioso e perigoso) — foi assim que "Reajustes" vazou para os
 * viewers, porque quem acrescentou o item ao grupo Clientes não mexeu no `if`
 * escrito à mão que era a única regra de filho que existia.
 *
 * `permission` é o MÓDULO, sem sufixo: o filtro checa `<módulo>:view` e
 * `<módulo>:view_own` via `canAccessModule`.
 */

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  permission: string;
  children?: MenuItem[];
}

export const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'chart', permission: 'dashboard' },
  { id: 'leads', label: 'CRM', icon: 'userPlus', permission: 'leads' },
  // A1 (26/10/2026, decisão do Heitor): o Núcleo é o conteúdo das propostas comerciais, então mora junto do CRM, e não
  // no bloco de e-mail. Item próprio logo abaixo, e não CRM virando grupo: o board segue a um clique para quem vende.
  // `nucleo-propostas` NÃO existe na tabela permissions: falha fechado, só o admin vê (cada linha daqui sai impressa
  // numa proposta). Fora do bloco admin a permissão passa a valer de verdade, por isso não é mais `email`.
  { id: 'nucleo-propostas', label: 'Núcleo de propostas', icon: 'shield', permission: 'nucleo-propostas' },
  { id: 'products', label: 'Produtos', icon: 'package', permission: 'products' },
  { id: 'clients', label: 'Clientes', icon: 'users', permission: 'clients', children: [
    { id: 'clients', label: 'Lista', icon: 'list', permission: 'clients' },
    // Grupos Econômicos e DDRs seguem em `clients` de propósito: são dados do próprio
    // cliente, e código próprio aqui tiraria de viewer um acesso legítimo.
    { id: 'locations', label: 'Grupos Econômicos', icon: 'mapPin', permission: 'clients' },
    { id: 'ddrs', label: 'DDRs', icon: 'phone', permission: 'clients' },
    // Assinaturas era escondida por um `if` à mão que só olhava role==='viewer'.
    // Agora é permissão como qualquer outro item.
    //
    // `contracts` é o MÓDULO: o filtro aceita contracts:view (todos) OU
    // contracts:view_own (só os contratos dos clientes do próprio usuário), que
    // o backend implementa de verdade em contracts.service.ts. Trocar isto por
    // uma checagem de código cru (`contracts:view`) tiraria a tela de quem tem
    // só o próprio e deixaria o filtro do backend inalcançável — é o caso de
    // quem fecha cliente e precisa ver as assinaturas que fechou.
    { id: 'contracts', label: 'Assinaturas', icon: 'clipboard', permission: 'contracts' },
    // Reajustes NÃO é `contracts`: o backend tornou contract-adjustments
    // ADMIN-only, leitura E escrita (decisão de 12/09/2026 — reajuste é valor
    // puro). Com `contracts` o item voltaria a aparecer para quem tem
    // contracts:view_own e cairia em tela vazia, que é o mesmo defeito de
    // origem, só com outro gatilho. Código próprio, sem correspondente no
    // backend, mais fail-closed = só admin. Vira `reajustes:view` no dia em que
    // o backend trocar @Roles por permissão.
    { id: 'reajustes', label: 'Reajustes', icon: 'trendingUp', permission: 'reajustes' },
  ]},
  // ⚠ O Financeiro NÃO é um bloco homogêneo. Conferido no backend em e4c3434
  // (12/09/2026), controller por controller:
  //
  //   invoices.controller          → só JwtAuthGuard, sem RolesGuard
  //                                  → Recebíveis e Faturas abrem para qualquer
  //                                    autenticado: `invoices` é gate suficiente.
  //   fiscal-document.controller   → 13 rotas ADMIN+EDITOR, 3 só ADMIN
  //   cnab.controller (Boletos)    → 4 rotas ADMIN+EDITOR, 7 só ADMIN
  //                                  → editor passa; `invoices` serve.
  //   expenses.controller          → 47 de 47 rotas @Roles(ADMIN)
  //   bank-accounts.controller     → 15 de 15 @Roles(ADMIN)
  //   loan-contracts.controller    → 13 de 13 @Roles(ADMIN)
  //
  // Os itens cujas fontes de dados são 100% ADMIN levam código PRÓPRIO, que
  // NÃO EXISTE na tabela permissions — e é isso que os esconde, porque o filtro
  // falha fechado e só o bypass de admin passa.
  //
  // ⛔ NÃO CRIE esses códigos no banco achando que conserta um bug. Criar
  // `despesas:view` e conceder a um editor faz o item aparecer e o backend
  // responder 403 — que é exatamente o defeito que isto corrige. O caminho
  // certo, no dia em que um não-admin tiver que ver despesas, é o backend
  // trocar @Roles(ADMIN) por permissão; aí o código passa a existir e o item
  // aparece sozinho.
  //
  // O pai continua `invoices` porque QUATRO filhos continuam alcançáveis por
  // editor (Recebíveis, Faturas, Notas Fiscais, Boletos) — não vira item vazio.
  { id: 'financeiro', label: 'Financeiro', icon: 'dollar', permission: 'invoices', children: [
    // Resumo puxa expenses.summary + bank-accounts + expenses.cash-flow: as três
    // são ADMIN. Não estava na lista que me passaram; achei conferindo o que a
    // tela chama. Sem isto, editor abriria um dashboard 100% vazio.
    { id: 'resumo-financeiro', label: 'Resumo', icon: 'home', permission: 'resumo-financeiro' },
    // Recebíveis é SÓ ADMIN (decisão do Heitor, 23/10/2026): a tela mostra os totais da carteira. O código `recebiveis`
    // não é dado a ninguém, então só o admin (que vê tudo) passa; a URL direta cai no mesmo filtro (podeVerPor).
    { id: 'recebiveis', label: 'Recebiveis', icon: 'trendingUp', permission: 'recebiveis' },
    { id: 'invoices', label: 'Faturas', icon: 'fileText', permission: 'invoices' },
    // Cobrança (E1, 30/10/2026): a mesma permissão de Faturas. As rotas de leitura da cobrança aceitam vendedor e
    // leitor; as de escrita são decididas por papel no backend, e a tela esconde o botão de quem receberia 403.
    { id: 'cobranca', label: 'Cobrança', icon: 'alertCircle', permission: 'invoices' },
    // Fiscal e Boletos são ADMIN+EDITOR por ROLE, não por permissão: um VIEWER
    // que um dia receba invoices:view_own veria estes itens e tomaria 403.
    // Fica registrado; não inventei código novo porque hoje viewer não recebe
    // invoices, e o default novo dá invoices:view_own só a editor.
    { id: 'fiscal', label: 'Notas Fiscais', icon: 'shield', permission: 'invoices', children: [
      { id: 'fiscal-docs', label: 'Documentos', icon: 'fileText', permission: 'invoices' },
      { id: 'fiscal-certs', label: 'Certificados', icon: 'shield', permission: 'invoices' },
      { id: 'fiscal-monitor', label: 'Monitoramento', icon: 'chart', permission: 'invoices' },
    ]},
    { id: 'boletos', label: 'Boletos', icon: 'receipt', permission: 'invoices' },
    { id: 'despesas', label: 'Despesas', icon: 'dollar', permission: 'despesas' },
    { id: 'contas-bancarias', label: 'Contas', icon: 'building', permission: 'contas-bancarias' },
    { id: 'fluxo-caixa', label: 'Fluxo de Caixa', icon: 'chart', permission: 'fluxo-caixa' },
    { id: 'emprestimos', label: 'Op. de Crédito', icon: 'dollar', permission: 'emprestimos' },
  ]},
  { id: 'licitacoes', label: 'Licitações', icon: 'search', permission: 'licitacoes', children: [
    { id: 'lic-encontrar', label: 'Encontrar', icon: 'search', permission: 'licitacoes' },
    { id: 'lic-cadastrar', label: 'Cadastrar', icon: 'fileText', permission: 'licitacoes' },
    { id: 'lic-disputar', label: 'Disputar', icon: 'target', permission: 'licitacoes' },
    { id: 'lic-monitorar', label: 'Monitorar', icon: 'eye', permission: 'licitacoes' },
    { id: 'lic-documentos', label: 'Documentos', icon: 'shield', permission: 'licitacoes' },
    { id: 'lic-acessos', label: 'Acessos', icon: 'key', permission: 'licitacoes' },
    { id: 'comprasnet-infra', label: 'Automação', icon: 'activity', permission: 'licitacoes' },
  ]},
  { id: 'demandas', label: 'Demandas', icon: 'clipboard', permission: 'demandas' },
  { id: 'administrativo', label: 'Administrativo', icon: 'briefcase', permission: 'materials', children: [
    // Ofícios e Portabilidade NÃO têm permission code no backend: as rotas são
    // @Roles(ADMIN) puro. Códigos que não existem, mais fail-closed, escondem os
    // itens de todo mundo menos admin — que é exatamente o que o backend já
    // responde (403 para editor). Não é regressão: é o menu parando de oferecer
    // porta que não abre. Vira `oficios:view`/`portabilidade:view` no dia em que
    // o backend trocar o guard de role por permissão.
    { id: 'oficios', label: 'Ofícios', icon: 'fileText', permission: 'oficios' },
    // A1 (26/10/2026): Comunicados é comunicação formal com o cliente, como Ofícios e Portabilidade. Envia em massa (a
    // ~191 clientes), então segue só admin: `comunicados` não existe na tabela permissions e falha fechado.
    { id: 'email-comunicados', label: 'Comunicados', icon: 'mail', permission: 'comunicados' },
    { id: 'portabilidade', label: 'Portabilidade', icon: 'phone', permission: 'portabilidade' },
    { id: 'materials', label: 'Materiais', icon: 'image', permission: 'materials' },
  ]},
  { id: 'infraestrutura', label: 'Infraestrutura', icon: 'server', permission: 'reservations', children: [
    { id: 'aws-reservations', label: 'Reservas AWS', icon: 'cloud', permission: 'reservations' },
    { id: 'ec2-monitoring', label: 'Monitor EC2', icon: 'activity', permission: 'reservations' },
  ]},
];

/**
 * Bloco Admin. Só é renderizado dentro de `user?.role === 'admin'`, e admin
 * passa por cima de qualquer permissão — então estes códigos não mudam nada
 * hoje. Estão aqui porque o guardrail não tem exceção: exceção em guardrail é o
 * tipo de buraco que gerou este trabalho.
 *
 * A1 (26/10/2026, M1): como na Central, o bloco se divide em seções com título, e o Email deixa de ser submenu. A
 * Comunicação fica com o que configura os e-mails do sistema INTEIRO (fatura, cobrança, licitação): mudar um template
 * muda o e-mail de fatura, por isso Templates, Contas, Automações e Logs seguem aqui, só admin. Os rótulos ganham o
 * complemento ("de e-mail", "de envio") porque saíram de baixo de "Email".
 */
export interface SecaoDoMenu {
  titulo: string;
  itens: MenuItem[];
}

export const adminSecoes: SecaoDoMenu[] = [
  { titulo: 'Análise', itens: [
    { id: 'reports', label: 'Relatórios', icon: 'chart', permission: 'reports' },
  ]},
  { titulo: 'Comunicação', itens: [
    // DÍVIDA: a Camilla tem `templates`, mas o item vive no bloco admin — a permissão não abre tela para ela.
    { id: 'templates', label: 'Templates', icon: 'fileText', permission: 'email' },
    { id: 'email-contas', label: 'Contas de e-mail', icon: 'settings', permission: 'email' },
    { id: 'email-automacoes', label: 'Automações', icon: 'clock', permission: 'email' },
    { id: 'email-logs', label: 'Logs de envio', icon: 'chart', permission: 'email' },
  ]},
  { titulo: 'Empresa e acesso', itens: [
    { id: 'users', label: 'Usuários', icon: 'shield', permission: 'users' },
    { id: 'docs-societarios', label: 'Docs Societários', icon: 'fileText', permission: 'corporate_docs' },
    { id: 'integrations', label: 'Integrações API', icon: 'server', permission: 'integrations' },
    { id: 'settings', label: 'Configurações', icon: 'settings', permission: 'settings' },
  ]},
];

/** O bloco admin achatado, na ordem das seções: é o que a árvore das rotas e o guardrail percorrem. */
export const adminItems: MenuItem[] = adminSecoes.flatMap((s) => s.itens);

/**
 * Filtra a árvore por permissão, recursivamente e FALHANDO FECHADO.
 *
 * Regras, nesta ordem:
 *  1. item sem `permission` declarada → escondido (inclusive para admin: o
 *     guardrail garante que não acontece, e se acontecer o erro tem que doer
 *     em alguém que consiga reportá-lo).
 *  2. sem acesso ao módulo do próprio item → escondido, com os filhos junto.
 *  3. item que TEM filhos e ficou sem NENHUM filho visível → escondido também.
 *     Sem isto ele viraria folha clicável apontando para uma section que não
 *     existe ('administrativo', 'financeiro'…), ou seja, tela em branco.
 */
export function filterMenuByPermission(
  items: MenuItem[],
  canAccessModule: (module: string) => boolean,
): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items) {
    if (!item.permission) continue;
    if (!canAccessModule(item.permission)) continue;
    if (item.children && item.children.length > 0) {
      const children = filterMenuByPermission(item.children, canAccessModule);
      if (children.length === 0) continue;
      out.push({ ...item, children });
    } else {
      out.push(item);
    }
  }
  return out;
}
